import LayerIcon from '@mui/icons-material/LayersOutlined';
import MapTool from '@apps-common/ui-maps/MapTool';
import React, {useCallback, useState} from 'react';
import SelectAOIIcon from '@apps-common/ui-icons/SelectAoi';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {array, bool, func, object, string} from 'prop-types';
import {connect} from 'react-redux';

import AdditionalZoomTools from '@explorer/components/map/additional-zoom-tools';
import DrawTools from '@explorer/components/map/draw-tools';
import LayerTools from '@explorer/components/map/layer-tools';
import LinkShareTool from '@explorer/components/map/share-tool';
import MapToolsGroup from '@explorer/components/map/map-tools-group';
import ZoomControls from '@explorer/components/map/zoom-controls';
import {
  DEFAULT_ZOOM,
  MAP_DRAW_LAYERS,
  MAP_TOOLS,
  MAX_FOLDERS_ZOOM,
  MAX_ZOOM,
  TILE_MODE,
  TOUR_STAGES,
  UI_MODES,
} from '@explorer/constants';
import {RulerIcon} from '@explorer/components/map/ruler-icon';
import {clearSearch, defineGeometry} from '@explorer/actions/search';
import {measureTooltipMap} from '@explorer/components/map/draw-tools/tooltips';

const MENUS = {
  MEASURE: 'measure-menu',
  AOI: 'aoi-menu',
  LAYERS: 'layers-menu',
  ZOOM: 'zoom-menu',
};

const MapControls = ({
  clearSearch,
  defineGeometry,
  isComparing,
  measureFeatures,
  searchFeatures,
  setHoverFeatures,
  setMeasureFeatures,
  setSearchFeatures,
  setTempGeometry,
  tempGeometry,
  tileMode,
}) => {
  const [menu, setMenu] = useState('');
  const [menuTarget, setMenuTarget] = useState(null);

  const olMap = {};

  const toggleMenu = useCallback(
    (nextMenu, anchor) => {
      if (nextMenu === menu) {
        setMenu('');
      } else {
        setMenuTarget(anchor);
        setMenu(nextMenu);
      }
    },
    [menu, setMenu, setMenuTarget]
  );

  const deleteMeasureFeature = useCallback(
    feature => {
      setMeasureFeatures(measureFeatures.filter(f => f.id !== feature.id));
    },
    [measureFeatures, setMeasureFeatures]
  );

  const changeMeasureFeatureVisibility = useCallback(
    feature => {
      setMeasureFeatures(
        measureFeatures.map(f => {
          if (f.id === feature.id) {
            const fprops = f.properties || {};

            f.properties = {
              ...fprops,
              visible: !(
                fprops.visible === undefined || fprops.visible === true
              ),
            };
          }
          return f;
        })
      );
    },
    [measureFeatures, setMeasureFeatures]
  );

  const onHoverFeature = useCallback(
    feature => {
      if (feature) {
        setHoverFeatures([feature]);
      } else {
        setHoverFeatures([]);
      }
    },
    [setHoverFeatures]
  );

  const showDrawingTools = !isComparing && tileMode !== TILE_MODE.FOLDERS;

  return (
    <React.Fragment>
      <MapToolsGroup>
        <LinkShareTool />
        {menu === MENUS.AOI && (
          <DrawTools
            anchorEl={menuTarget}
            open
            features={searchFeatures}
            tempGeometry={tempGeometry}
            setTempGeometry={setTempGeometry}
            placement="top-end"
            drawLayer={MAP_DRAW_LAYERS.GEOMETRY}
            onDelete={() => {
              clearSearch();
            }}
            canToggleVisibility={false}
            uploadAvailable
            onSave={() => {
              if (tempGeometry) {
                setSearchFeatures([
                  {type: 'Feature', id: 'tmp-id', geometry: tempGeometry},
                ]);

                defineGeometry({geometry: tempGeometry});
              }
            }}
            onHover={onHoverFeature}
          />
        )}

        {showDrawingTools && (
          <MapTool
            data-tour={TOUR_STAGES.DRAW_TOOLS}
            data-qe="aoi-tool"
            key="aoi-toggle"
            tooltipTitle="Draw or upload an area of interest"
            active={menu === MENUS.AOI}
            icon={<SelectAOIIcon />}
            onClick={evt => {
              toggleMenu(MENUS.AOI, evt.target);
            }}
          />
        )}

        {menu === MENUS.MEASURE && (
          <DrawTools
            anchorEl={menuTarget}
            open
            tempGeometry={tempGeometry}
            setTempGeometry={setTempGeometry}
            features={measureFeatures}
            setFeatures={setMeasureFeatures}
            placement="top-end"
            drawLayer={MAP_DRAW_LAYERS.MEASURE}
            title="Measure"
            toolsAvailable={[MAP_TOOLS.DRAW_POLYGON, MAP_TOOLS.DRAW_LINE]}
            tooltips={measureTooltipMap}
            onDelete={deleteMeasureFeature}
            onChangeVisibility={changeMeasureFeatureVisibility}
            onHover={onHoverFeature}
            onSave={feature => {
              if (tempGeometry) {
                for (let i = 0, ii = measureFeatures.length; i < ii; i++) {
                  if (measureFeatures[i].id === feature.id) {
                    measureFeatures[i] = {
                      ...feature,
                      geometry: tempGeometry,
                    };
                  }
                }
                setMeasureFeatures(measureFeatures);
              }
            }}
          />
        )}

        <MapTool
          data-qe="measure-tool"
          key="measure-toggle"
          tooltipTitle="Measure areas and distances"
          tooltipPosition="left-end"
          active={menu === MENUS.MEASURE}
          icon={<RulerIcon />}
          onClick={evt => {
            toggleMenu(MENUS.MEASURE, evt.target);
          }}
        />
      </MapToolsGroup>

      <MapToolsGroup>
        {menu === MENUS.LAYERS && <LayerTools open anchorEl={menuTarget} />}
        <MapTool
          data-qe="overlay-tool"
          key="overlay-toggle"
          tooltipTitle="Toggle map overlays"
          tooltipPosition="left-end"
          active={menu === MENUS.LAYERS}
          icon={<LayerIcon />}
          onClick={evt => {
            toggleMenu(MENUS.LAYERS, evt.target);
          }}
        />
      </MapToolsGroup>

      <MapToolsGroup>
        {menu === MENUS.ZOOM && (
          <AdditionalZoomTools
            open
            anchorEl={menuTarget}
            map={olMap}
            placement="left-end"
            onChangeTool={() => {
              toggleMenu('', null);
            }}
          />
        )}

        <ZoomControls
          maxZoom={tileMode === TILE_MODE.FOLDERS ? MAX_FOLDERS_ZOOM : MAX_ZOOM}
          minZoom={DEFAULT_ZOOM}
          additionalControls={
            <MapTool
              data-qe="zoom-tools"
              key="zoom-toggle"
              tooltipTitle="Zoom tools"
              tooltipPosition="left-end"
              active={menu === MENUS.ZOOM}
              icon={<ZoomInIcon />}
              onClick={evt => {
                toggleMenu(MENUS.ZOOM, evt.target);
              }}
              sx={{
                borderRadius: '0 0 4px 4px',
                boxShadow: 'none',
              }}
            />
          }
        />
      </MapToolsGroup>
    </React.Fragment>
  );
};

MapControls.propTypes = {
  clearSearch: func,
  defineGeometry: func,
  isComparing: bool,
  measureFeatures: array,
  searchFeatures: array,
  setHoverFeatures: func,
  setMeasureFeatures: func,
  setSearchFeatures: func,
  setTempGeometry: func,
  tempGeometry: object,
  tileMode: string,
};

const mapStateToProps = state => ({
  isComparing: state.ui.mode === UI_MODES.COMPARE,
  tileMode: state.tiles.mode,
});

const mapDispatchToProps = {
  clearSearch,
  defineGeometry,
};

export default connect(mapStateToProps, mapDispatchToProps)(MapControls);
